import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';

import { hasLength } from '@mtna/lib-ui';

import { RdsCodeDisplayType } from '../core/models';

@Component({
  selector: 'rds-tabulation-display-options',
  template: `
    <h4 class="mat-caption">CODE DISPLAY</h4>
    <mat-button-toggle-group
      #group="matButtonToggleGroup"
      class="display-option-group"
      multiple="true"
      [disabled]="disabled"
      (change)="handleToggleChange(group)"
    >
      <mat-button-toggle value="NAME" [checked]="value === 'NAME' || value === 'BOTH'">NAME</mat-button-toggle>
      <mat-button-toggle value="VALUE" [checked]="value === 'VALUE' || value === 'BOTH'">VALUE</mat-button-toggle>
    </mat-button-toggle-group>
  `,
  styles: [
    `
      rds-tabulation-display-options .mat-caption {
        font-weight: 500;
        margin-top: 0;
        margin-bottom: 8px;
      }
      rds-tabulation-display-options .display-option-group {
        margin-bottom: 16px;
      }
      /** Reduces the toggle group height to be aligned with default button height */
      rds-tabulation-display-options .display-option-group .mat-button-toggle .mat-button-toggle-button .mat-button-toggle-label-content {
        font-size: 14px;
        font-weight: 500;
        line-height: 36px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsTabulationDisplayOptionsComponent implements ControlValueAccessor {
  @Input()
  get value(): RdsCodeDisplayType {
    return this._value;
  }
  set value(value: RdsCodeDisplayType) {
    if (value !== this._value) {
      this._value = value || 'NAME';
      this.changeDetectorRef.markForCheck();
    }
  }
  private _value: RdsCodeDisplayType = 'NAME';

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this._disabled) {
      this._disabled = newValue;
      this.changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  @Output() readonly change = new EventEmitter<RdsCodeDisplayType>();

  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  constructor(
    elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
    private changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  handleToggleChange(group: MatButtonToggleGroup) {
    const newValue = group.value as Array<RdsCodeDisplayType>;
    if (hasLength(newValue)) {
      this.value = newValue.length > 1 ? 'BOTH' : newValue[0];
      this._emitChangeEvent();
    } else {
      group.value = this.value === 'BOTH' ? ['NAME', 'VALUE'] : [this.value];
    }
  }

  registerOnChange(fn: any): void {
    this._controlValueAccessorChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  _emitChangeEvent() {
    this._controlValueAccessorChangeFn(this.value);
    this.change.emit(this.value);
  }
}
